import React, { useState } from 'react';
import '@/App.css';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Sparkles, Image as ImageIcon, CheckCircle2, X } from 'lucide-react';
import ImageCapture from '@/components/ImageCapture';
import ColorChart from '@/components/ColorChart';
import HairAnalysisReport from '@/components/HairAnalysisReport';
import ColorPreview from '@/components/ColorPreview';
import MultiColorComparison from '@/components/MultiColorComparison';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import ManualMaskTool from '@/components/ManualMaskTool';
import { recolorImage } from '@/utils/imageProcessing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [personMask, setPersonMask] = useState(null);
  const [hairAnalysis, setHairAnalysis] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]);
  const [colorPreview, setColorPreview] = useState(null);
  const [multiColorResults, setMultiColorResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMasking, setIsMasking] = useState(false);

  const handleImageCapture = async (imageSrc, base64) => {
    setCapturedImage(imageSrc);
    setImageBase64(base64);
    setPersonMask(null);
    setIsMasking(true); // Enter masking step
  };

  const handleMaskGenerated = async (maskDataUrl) => {
    setPersonMask(maskDataUrl);
    setIsMasking(false);
    setCurrentStep(2);

    // Auto-analyze hair
    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze-hair`, {
        image_base64: imageBase64
      });
      setHairAnalysis(response.data);
      if (response.data.is_fallback) {
        toast.info('AI service busy — showing estimated analysis. Retry for AI-powered results.');
      } else {
        toast.success('Hair analysis complete!');
      }
    } catch (error) {
      console.error('Error analyzing hair:', error);
      toast.error('Failed to analyze hair. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleColorSelect = (shade) => {
    // Check if already selected
    const isSelected = selectedColors.some(c => c.code === shade.code);

    if (isSelected) {
      // Remove from selection
      setSelectedColors(prev => prev.filter(c => c.code !== shade.code));
    } else {
      // Add to selection
      setSelectedColors(prev => [...prev, shade]);
    }
  };

  const applySelectedColor = async () => {
    if (selectedColors.length === 0) {
      toast.error('Please select at least one color');
      return;
    }

    setLoading(true);

    // Always use compare flow (works for 1-3 colors)
    try {
      const colorCodes = selectedColors.map(c => c.code);
      const colorNames = selectedColors.map(c => c.name);
      const response = await axios.post(`${API}/compare-colors`, {
        image_base64: imageBase64,
        color_codes: colorCodes,
        color_names: colorNames
      });

      // Attach hex values and generate recolored images
      const processedResults = await Promise.all(response.data.results.map(async (r, i) => {
        const hex = selectedColors[i]?.hex || '#5D3C26';
        let modifiedImage = null;
        if (personMask) {
          modifiedImage = await recolorImage(capturedImage, personMask, hex);
        }
        return {
          ...r,
          hex,
          modified_image: modifiedImage || capturedImage // Fallback to original if processing fails
        };
      }));

      setMultiColorResults(processedResults);
      setCurrentStep(4);
      if (response.data.is_fallback) {
        toast.info('AI service busy — showing general comparison. Retry for personalized results.');
      } else {
        toast.success('Color comparison ready!');
      }
    } catch (error) {
      console.error('Error comparing colors:', error);
      toast.error('Failed to compare colors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetApp = () => {
    setCapturedImage(null);
    setImageBase64(null);
    setHairAnalysis(null);
    setSelectedColors([]);
    setColorPreview(null);
    setMultiColorResults([]);
    setCurrentStep(1);
  };

  const steps = [
    { number: 1, label: 'Capture', icon: ImageIcon },
    { number: 2, label: 'Analyze & Select', icon: Sparkles },
    { number: 3, label: 'Preview', icon: Palette },
  ];

  return (
    <div className="App">
      <Toaster position="top-center" />

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay" />
        <div className="relative z-10 container mx-auto px-6 py-12 md:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl text-text-primary font-light tracking-tight leading-none mb-6"
            >
              AI Hair Color Studio
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-sans text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto"
            >
              Discover your perfect shade with AI-powered analysis. Try professional salon colors virtually before you commit.
            </motion.p>
          </div>

          {/* Step Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center items-center space-x-4 md:space-x-8 mt-12"
            data-testid="step-indicators"
          >
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`step-indicator w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all ${currentStep >= step.number
                          ? 'bg-accent-primary text-white active'
                          : 'bg-bg-secondary text-text-muted'
                        }`}
                      data-testid={`step-${step.number}`}
                    >
                      {currentStep > step.number ? (
                        <CheckCircle2 className="h-6 w-6 md:h-8 md:h-8" />
                      ) : (
                        <Icon className="h-6 w-6 md:h-8 md:h-8" />
                      )}
                    </div>
                    <p className="font-sans text-xs md:text-sm text-text-secondary mt-2">
                      {step.label}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-0.5 w-12 md:w-24 ${currentStep > step.number ? 'bg-accent-primary' : 'bg-border-color'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 md:py-16">
        <AnimatePresence mode="wait">
          {/* Step 1: Image Capture */}
          {currentStep === 1 && !isMasking && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-8">
                <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-3">
                  Start with Your Photo
                </h2>
                <p className="font-sans text-text-secondary">
                  Upload a clear photo of your hair or take one now
                </p>
              </div>
              <ImageCapture onImageCapture={handleImageCapture} />
            </motion.div>
          )}

          {/* Masking Step */}
          {isMasking && (
            <motion.div
              key="masking"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="max-w-3xl mx-auto pb-32"
            >
              <div className="text-center mb-8">
                <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-3">
                  Mask Your Hair
                </h2>
                <p className="font-sans text-text-secondary">
                  Quickly paint over your hair to tell the AI exactly where to apply color.
                </p>
              </div>
              <ManualMaskTool
                imageSrc={capturedImage}
                onMaskGenerated={handleMaskGenerated}
              />
            </motion.div>
          )}

          {/* Step 2: Analysis & Color Selection */}
          {currentStep === 2 && !isMasking && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="max-w-7xl mx-auto pb-32"
            >
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
                {/* Image Preview */}
                <div className="lg:col-span-2">
                  <div className="sticky top-6 space-y-6">
                    <div className="relative rounded-sm overflow-hidden border border-border-color" data-testid="captured-image-preview">
                      <img
                        src={capturedImage}
                        alt="Your hair"
                        className="w-full h-auto"
                      />
                    </div>
                    <Button
                      onClick={resetApp}
                      variant="outline"
                      className="w-full border border-text-primary text-text-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-bg-secondary transition-colors"
                      data-testid="start-over-btn"
                    >
                      START OVER
                    </Button>
                  </div>
                </div>

                {/* Analysis & Color Selection */}
                <div className="lg:col-span-3 space-y-8">
                  <HairAnalysisReport analysis={hairAnalysis} loading={loading && !selectedColors.length} />

                  <div className="pt-8 border-t border-border-color">
                    <div className="text-center mb-6">
                      <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-2">
                        Choose Your Colors
                      </h2>
                      <p className="font-sans text-text-secondary">
                        Select 1 to 3 colors then hit Compare
                      </p>
                    </div>
                    <ColorChart
                      onColorSelect={handleColorSelect}
                      selectedColors={selectedColors}
                      onCompare={applySelectedColor}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>

              {/* ── Floating Bottom Action Bar ── */}
              {selectedColors.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-t border-border-color shadow-[0_-4px_24px_rgba(0,0,0,0.08)]" data-testid="floating-action-bar">
                  <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                    {/* Selected swatches */}
                    <div className="flex items-center space-x-3 overflow-x-auto">
                      {selectedColors.map((color, idx) => (
                        <button
                          key={color.code}
                          onClick={() => handleColorSelect(color)}
                          className="flex items-center space-x-2 bg-bg-secondary border border-border-color rounded-sm px-3 py-2 hover:border-accent-primary transition-colors shrink-0"
                          data-testid={`selected-chip-${color.code}`}
                        >
                          <div className="w-6 h-6 rounded-sm border border-border-color" style={{ backgroundColor: color.hex }} />
                          <span className="font-sans text-sm text-text-primary whitespace-nowrap">{color.code}</span>
                          <X className="h-3 w-3 text-text-muted" />
                        </button>
                      ))}
                      {selectedColors.length < 3 && (
                        <span className="font-sans text-xs text-text-muted whitespace-nowrap">
                          {3 - selectedColors.length} more available
                        </span>
                      )}
                    </div>

                    {/* Compare button */}
                    <Button
                      onClick={applySelectedColor}
                      disabled={loading}
                      className="shrink-0 ml-4 bg-accent-primary text-white px-8 py-3 font-sans text-sm tracking-widest uppercase hover:bg-accent-hover transition-colors"
                      data-testid="floating-compare-btn"
                    >
                      {loading ? 'Processing...' : `Compare ${selectedColors.length} Color${selectedColors.length > 1 ? 's' : ''}`}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3/4: Color Comparison (works for 1-3 colors) */}
          {(currentStep === 3 || currentStep === 4) && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="max-w-7xl mx-auto"
            >
              <MultiColorComparison
                originalImage={capturedImage}
                comparisons={multiColorResults}
                loading={loading}
                personMask={personMask}
              />

              <div className="flex justify-center space-x-4 mt-8">
                <Button
                  onClick={() => {
                    setSelectedColors([]);
                    setMultiColorResults([]);
                    setCurrentStep(2);
                  }}
                  variant="outline"
                  className="border border-text-primary text-text-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-bg-secondary transition-colors"
                  data-testid="select-different-colors-btn"
                >
                  TRY DIFFERENT COLORS
                </Button>
                <Button
                  onClick={resetApp}
                  variant="outline"
                  className="border border-text-primary text-text-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-bg-secondary transition-colors"
                  data-testid="new-analysis-btn"
                >
                  NEW PHOTO
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <footer className="bg-bg-secondary border-t border-border-color py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="font-sans text-sm text-text-muted">
            AI Hair Color Studio - Powered by advanced AI vision technology
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
